/**
 * v132 — Compare Overlay view.
 *
 * Multi-select picker (by session or by set) + single overlay target with
 * color-cycled shots + per-source aggregates legend + filters/sort.
 *
 * Mobile-first: defaults to vertically stacked, picker opens as a modal.
 */
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ShotSession } from '../types';
import type { RichShot } from '../v2-schema';
import type { ShotSet, OverlayLegendRow } from '../v3-schema';
import { computeOverlayStats } from '../aggregates';
import { migrateSessionToV3 } from '../migrate';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Check, Filter, Layers, Printer, Download, X, BarChart3 } from 'lucide-react';
import clsx from 'clsx';
import { buildShotsCsv, downloadCsv, isoToFilenameDate } from '../csv';
import { ShareButton } from '@/share/components/ShareButton';
import { buildCompareSnapshot } from '@/share/snapshotBuilders';

interface CompareViewProps {
  sessions: readonly ShotSession[];
}

type Mode = 'session' | 'set';
type SortKey = 'chronological' | 'meanRadius' | 'group95';

interface PickerSource {
  /** Source kind. */
  kind: 'session' | 'set';
  /** Stable id used as the React key + color hash input. */
  id: string;
  /** Session this source belongs to. */
  session: ShotSession;
  /** When kind=set, the set this represents. */
  set?: ShotSet;
  /** Display label. */
  label: string;
  /** Display sublabel — date + counts. */
  sublabel: string;
  /** The shots this source contributes to the overlay. */
  shots: RichShot[];
}

export function CompareView({ sessions }: CompareViewProps) {
  const [mode, setMode] = useState<Mode>('session');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sort, setSort] = useState<SortKey>('chronological');
  const [distanceFilter, setDistanceFilter] = useState<number | 'all'>('all');

  // Compute picker source list — sessions (always) and sets (when mode='set').
  const sources = useMemo<PickerSource[]>(() => {
    return buildSources(sessions, mode);
  }, [sessions, mode]);

  // Selected sources for overlay
  const selectedSources = useMemo<PickerSource[]>(() => {
    return sources.filter((s) => selected.has(s.id));
  }, [sources, selected]);

  const filteredSelectedSources = useMemo<PickerSource[]>(() => {
    if (distanceFilter === 'all') return selectedSources;
    return selectedSources.map((src) => ({
      ...src,
      shots: src.shots.filter((s) => s.distanceYd === distanceFilter),
    }));
  }, [selectedSources, distanceFilter]);

  // Sort applied to legend & rendering order
  const sortedSources = useMemo<PickerSource[]>(() => {
    const arr = [...filteredSelectedSources];
    if (sort === 'chronological') {
      arr.sort((a, b) => {
        const ta = a.set?.startedAt ?? a.session.loggedAt;
        const tb = b.set?.startedAt ?? b.session.loggedAt;
        return ta.localeCompare(tb);
      });
    } else {
      const legendBy = (src: PickerSource) => {
        const aggs = computeOverlayStats([{ sourceId: src.id, label: src.label, shots: src.shots }])[0]?.aggregates;
        if (!aggs) return Infinity;
        return sort === 'meanRadius' ? aggs.meanRadiusIn : aggs.group95RadiusIn;
      };
      arr.sort((a, b) => legendBy(a) - legendBy(b));
    }
    return arr;
  }, [filteredSelectedSources, sort]);

  const legend = useMemo<OverlayLegendRow[]>(() => {
    return computeOverlayStats(sortedSources.map((s) => ({ sourceId: s.id, label: s.label, shots: s.shots })));
  }, [sortedSources]);

  // Available distances (filter chip set)
  const availableDistances = useMemo(() => {
    const dist = new Set<number>();
    selectedSources.forEach((src) => src.shots.forEach((s) => dist.add(s.distanceYd)));
    return Array.from(dist).sort((a, b) => a - b);
  }, [selectedSources]);

  const totalShots = legend.reduce((a, l) => a + l.shotCount, 0);

  const exportOverlayCsv = () => {
    // Bundle all selected sources into a single shot-level CSV with source columns.
    // Re-purpose buildShotsCsv by promoting set_id/set_label as session-level decoration.
    const fakeSessions = sortedSources.map((src) => ({
      ...src.session,
      id: src.id,
      richShots: src.shots,
    }));
    const csv = buildShotsCsv(fakeSessions);
    downloadCsv(`sparrow-overlay-${isoToFilenameDate()}.csv`, csv);
  };

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
            <Layers className="text-ember-500" size={18} />Compare overlay
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">A/B-test setup changes at the shot level.</p>
        </div>
        <div className="flex gap-2">
          <div className="inline-flex border border-zinc-800 rounded overflow-hidden">
            <button
              onClick={() => { setMode('session'); setSelected(new Set()); }}
              className={clsx('px-3 py-1.5 text-[12px] font-mono uppercase tracking-wider min-h-[44px] touch-manipulation', mode === 'session' ? 'bg-cyan-500/20 text-cyan-300' : 'text-zinc-400 hover:bg-zinc-900')}
            >By session</button>
            <button
              onClick={() => { setMode('set'); setSelected(new Set()); }}
              className={clsx('px-3 py-1.5 text-[12px] font-mono uppercase tracking-wider min-h-[44px] touch-manipulation', mode === 'set' ? 'bg-cyan-500/20 text-cyan-300' : 'text-zinc-400 hover:bg-zinc-900')}
            >By set</button>
          </div>
          <Button size="sm" variant="secondary" leftIcon={<Filter size={12} />} onClick={() => setPickerOpen(true)}>
            Select sources ({selected.size})
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {selectedSources.length === 0 && (
        <Card padding="lg" className="text-center border-dashed">
          <p className="text-sm text-zinc-400">Select two or more {mode === 'set' ? 'sets' : 'sessions'} to overlay them on one target.</p>
          <p className="text-[12px] text-zinc-500 mt-2">Shoot a group → tweak something → shoot another group → tap "By set", select both, see the difference.</p>
          <Button size="sm" className="mt-3" onClick={() => setPickerOpen(true)}>Pick sources</Button>
        </Card>
      )}

      {selectedSources.length > 0 && (
        <>
          {/* Filters strip */}
          <div className="flex flex-wrap items-center gap-2 text-[12px]">
            <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Filter</span>
            {availableDistances.length > 1 && (
              <div className="inline-flex flex-wrap gap-1">
                <button
                  onClick={() => setDistanceFilter('all')}
                  className={clsx('px-2 py-1 rounded border touch-manipulation', distanceFilter === 'all' ? 'bg-ember-500/20 border-ember-500/60 text-ember-300' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600')}
                >All</button>
                {availableDistances.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDistanceFilter(d)}
                    className={clsx('px-2 py-1 rounded border font-mono touch-manipulation', distanceFilter === d ? 'bg-ember-500/20 border-ember-500/60 text-ember-300' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600')}
                  >{d}yd</button>
                ))}
              </div>
            )}
            <span className="text-zinc-700 px-1">·</span>
            <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Sort</span>
            <div className="inline-flex border border-zinc-800 rounded overflow-hidden text-[11px] font-mono uppercase tracking-wider">
              {(['chronological','meanRadius','group95'] as SortKey[]).map((k) => (
                <button
                  key={k}
                  onClick={() => setSort(k)}
                  className={clsx('px-2 py-1', sort === k ? 'bg-cyan-500/15 text-cyan-300' : 'text-zinc-400 hover:bg-zinc-900')}
                >{k === 'chronological' ? 'Time' : k === 'meanRadius' ? 'Mean r' : '95% r'}</button>
              ))}
            </div>
            <span className="ml-auto text-[11px] font-mono text-zinc-500">{totalShots} shots · {legend.length} sources</span>
          </div>

          {/* Overlay target */}
          <Card padding="md" className="bg-zinc-900/30">
            <OverlayTargetSvg sources={sortedSources} colors={Object.fromEntries(legend.map((l) => [l.sourceId, l.color]))} />
          </Card>

          {/* Legend + per-source aggregates */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1">
              <BarChart3 size={10} /> Legend &amp; aggregates
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <th className="text-left py-2 px-2"></th>
                    <th className="text-left py-2 px-2">Source</th>
                    <th className="text-right py-2 px-2">Shots</th>
                    <th className="text-right py-2 px-2">Mean r</th>
                    <th className="text-right py-2 px-2">95% r</th>
                    <th className="text-right py-2 px-2">Center</th>
                  </tr>
                </thead>
                <tbody>
                  {legend.map((row) => (
                    <tr key={row.sourceId} className="border-b border-zinc-900">
                      <td className="py-2 px-2"><span className="inline-block w-3 h-3 rounded-full" style={{ background: row.color, boxShadow: '0 0 0 1px rgba(255,255,255,0.4)' }} /></td>
                      <td className="py-2 px-2 text-zinc-200 truncate max-w-[260px]">{row.label}</td>
                      <td className="py-2 px-2 text-right font-mono tabular-nums">{row.shotCount}</td>
                      <td className="py-2 px-2 text-right font-mono tabular-nums">{row.aggregates ? `${row.aggregates.meanRadiusIn.toFixed(2)}″` : '—'}</td>
                      <td className="py-2 px-2 text-right font-mono tabular-nums">{row.aggregates ? `${row.aggregates.group95RadiusIn.toFixed(2)}″` : '—'}</td>
                      <td className="py-2 px-2 text-right font-mono tabular-nums text-zinc-500">{row.aggregates ? `${row.aggregates.centerX.toFixed(2)}, ${row.aggregates.centerY.toFixed(2)}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Export actions */}
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              onClick={exportOverlayCsv}
              className="text-[12px] text-zinc-400 hover:text-cyan-300 flex items-center gap-1.5 px-2 py-1.5 border border-zinc-800 rounded touch-manipulation"
              title="Export overlay shots as CSV"
            >
              <Download size={11} /> Overlay.csv
            </button>
            <button
              onClick={() => typeof window !== 'undefined' && window.print()}
              className="text-[12px] text-zinc-400 hover:text-cyan-300 flex items-center gap-1.5 px-2 py-1.5 border border-zinc-800 rounded touch-manipulation"
              title="Print 1:1 overlay (use browser print, scale 100%)"
            >
              <Printer size={11} /> Print 1:1
            </button>
            {/* v135 — Share the overlay as an OG card. Lazy snapshot so we
                don't pay the cost while the user is still picking sources. */}
            <ShareButton
              getSnapshot={() => {
                const colorBySourceId = new Map(legend.map((l) => [l.sourceId, l.color]));
                return buildCompareSnapshot(
                  sortedSources.map((src, i) => ({
                    label: src.label,
                    color:
                      colorBySourceId.get(src.id) ??
                      ['#f59e0b', '#22d3ee', '#10b981', '#f43f5e'][i % 4],
                    shots: src.shots.map((s) => ({ xIn: s.hitXIn, yIn: s.hitYIn })),
                  })),
                  distanceFilter === 'all' ? null : distanceFilter,
                );
              }}
              label="Share overlay"
              className="text-[12px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1.5 px-2 py-1.5 border border-cyan-500/40 rounded touch-manipulation"
            />
          </div>
        </>
      )}

      {pickerOpen && (
        <PickerModal
          sources={sources}
          selected={selected}
          mode={mode}
          onClose={() => setPickerOpen(false)}
          onToggle={(id) => {
            setSelected((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            });
          }}
          onClear={() => setSelected(new Set())}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Overlay target SVG
// ─────────────────────────────────────────────────────────────────────────

function OverlayTargetSvg({
  sources, colors, halfExtentIn = 6,
}: {
  sources: readonly PickerSource[];
  colors: Record<string, string>;
  halfExtentIn?: number;
}) {
  const VIEW = halfExtentIn * 2 * 10;
  const HALF = VIEW / 2;
  const rings = [5.5, 4, 2.5, 1.25, 0.5];
  return (
    <svg viewBox={`0 0 ${VIEW} ${VIEW}`} className="w-full max-w-2xl mx-auto bg-zinc-950 rounded-lg border border-zinc-800" style={{ aspectRatio: '1 / 1' }} role="img" aria-label="Compare overlay target with shots from selected sources">
      <rect x={0} y={0} width={VIEW} height={VIEW} fill="#0a0a0a" />
      {rings.map((rIn, i) => {
        const r = rIn * 10;
        const isInner = i === rings.length - 1;
        return (
          <circle key={i} cx={HALF} cy={HALF} r={r}
            fill={isInner ? '#7c2d12' : 'none'} stroke={isInner ? '#fb923c' : '#52525b'}
            strokeWidth={isInner ? 1.2 : 0.8} opacity={isInner ? 0.9 : 0.5} />
        );
      })}
      <line x1={0} y1={HALF} x2={VIEW} y2={HALF} stroke="#3f3f46" strokeWidth={0.4} strokeDasharray="3 3" />
      <line x1={HALF} y1={0} x2={HALF} y2={VIEW} stroke="#3f3f46" strokeWidth={0.4} strokeDasharray="3 3" />

      {sources.map((src) => {
        const color = colors[src.id] ?? '#22d3ee';
        return src.shots.map((s) => {
          const cx = HALF + s.hitXIn * 10;
          const cy = HALF + s.hitYIn * 10;
          return (
            <g key={`${src.id}-${s.id}`}>
              <circle cx={cx} cy={cy} r={1.6} fill={color} stroke="#fafafa" strokeWidth={0.4} opacity={0.92} />
            </g>
          );
        });
      })}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Picker modal
// ─────────────────────────────────────────────────────────────────────────

function PickerModal({
  sources, selected, mode, onClose, onToggle, onClear,
}: {
  sources: readonly PickerSource[];
  selected: ReadonlySet<string>;
  mode: Mode;
  onClose: () => void;
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4" style={{ height: '100dvh' }} onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full max-w-lg bg-zinc-950 border-t sm:border border-zinc-800 sm:rounded-lg shadow-2xl flex flex-col" style={{ maxHeight: '100dvh' }} onClick={(e) => e.stopPropagation()}>
        <header className="flex-shrink-0 px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-cyan-400">Select sources</p>
            <h2 className="text-base font-bold">Pick {mode === 'set' ? 'sets' : 'sessions'} to overlay</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-100 w-10 h-10 flex items-center justify-center touch-manipulation"><X size={16} /></button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {sources.length === 0 && (
            <p className="text-sm text-zinc-500 text-center py-8">No {mode === 'set' ? 'sets' : 'sessions'} with shot data yet. Log a live session first.</p>
          )}
          {sources.map((src) => {
            const isSel = selected.has(src.id);
            return (
              <button
                key={src.id}
                onClick={() => onToggle(src.id)}
                className={clsx(
                  'w-full text-left px-3 py-2.5 rounded mb-1 border flex items-start gap-3 touch-manipulation',
                  isSel ? 'border-ember-500/60 bg-ember-500/5' : 'border-zinc-800 hover:border-zinc-600 bg-zinc-900/30',
                )}
              >
                <div className={clsx(
                  'mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0',
                  isSel ? 'border-ember-500 bg-ember-500/20' : 'border-zinc-700',
                )}>
                  {isSel && <Check size={12} className="text-ember-500" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-zinc-100 truncate">{src.label}</p>
                  <p className="text-[11px] text-zinc-500">{src.sublabel}</p>
                </div>
              </button>
            );
          })}
        </div>
        <footer className="flex-shrink-0 px-5 py-3 border-t border-zinc-800 flex justify-between gap-2 bg-zinc-950">
          <button onClick={onClear} className="text-[12px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-300 touch-manipulation">Clear</button>
          <Button size="sm" onClick={onClose}>Done ({selected.size})</Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Source builder
// ─────────────────────────────────────────────────────────────────────────

function buildSources(sessions: readonly ShotSession[], mode: Mode): PickerSource[] {
  const list: PickerSource[] = [];
  for (const raw of sessions) {
    const session = migrateSessionToV3(raw);
    const shots = session.richShots ?? [];
    if (shots.length === 0) continue;

    if (mode === 'session') {
      list.push({
        kind: 'session',
        id: session.id,
        session,
        label: session.location || `${session.distanceYd} yd · ${dateLabel(session.loggedAt)}`,
        sublabel: `${shots.length} shots · ${session.setupSnapshot?.setupName ?? 'no setup snapshot'}`,
        shots,
      });
    } else {
      const sets = session.sets ?? [];
      const shotsById = new Map(shots.map((s) => [s.id, s]));
      sets.forEach((set, idx) => {
        const setShots = set.shotIds.map((id) => shotsById.get(id)).filter((s): s is RichShot => Boolean(s));
        if (setShots.length === 0) return;
        const baseLabel = set.label || `Set ${idx + 1}`;
        list.push({
          kind: 'set',
          id: `${session.id}::${set.id}`,
          session,
          set,
          label: `${baseLabel} — ${session.location || dateLabel(session.loggedAt)}`,
          sublabel: `${setShots.length} shots${set.setupDelta ? ` · ${set.setupDelta}` : ''}`,
          shots: setShots,
        });
      });
    }
  }
  return list;
}

function dateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
